import { Arc } from '@mnd-frontend/charts';
import { Text, media } from '@mnd-frontend/ui';
import { localizeValue, sum } from '@mnd-frontend/utils';
import { type PieArcDatum, pie as d3pie } from 'd3-shape';
import styled from 'styled-components';
import { useInteraction } from '../../contexts/InteractionContext';

export const DonutContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 2rem;
  > * {
    width: 100%;
  }
  ${media.lessThan('desktop')} {
    flex-wrap: wrap;
    padding: 0rem;
  }
`;

const ChartLayer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledSvg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
`;

const TextLayer = styled.div`
  pointer-events: none;
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

interface DataPoint {
  id: string;
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data: DataPoint[];
  unit?: string;
}

export const DonutChart: React.FC<DonutChartProps> = ({ data, unit }) => {
  const { setHovered, hovered, onClick } = useInteraction();
  const [width, height] = [400, 400];
  const radius = Math.min(width, height) / 2;
  const pieGenerator = d3pie<DataPoint>()
    .value(d => d.value)
    .sort(null);
  const arcs = pieGenerator(data);
  const active = arcs.filter(arc => arc.data.id === hovered);

  return (
    <ChartLayer>
      <StyledSvg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
        <g transform={`translate(${width / 2} ${height / 2})`}>
          {arcs.map((arc: PieArcDatum<DataPoint>, i: number) => (
            <Arc
              key={`arc_${i}`}
              fill={arc.data.color}
              innerRadius={radius * 0.64}
              outerRadius={radius}
              startAngle={arc.startAngle}
              endAngle={arc.endAngle}
              $opacity={hovered === arc.data.id ? 1 : 0}
            />
          ))}
          {arcs.map((arc: PieArcDatum<DataPoint>, i: number) => (
            <Arc
              key={`arc_${i}`}
              fill={arc.data.color}
              innerRadius={radius * 0.64}
              outerRadius={radius - 6}
              startAngle={arc.startAngle}
              endAngle={arc.endAngle}
              $clickable={true}
              onMouseEnter={() => {
                arc.data.id !== hovered && setHovered(arc.data.id);
              }}
              onMouseLeave={() => {
                arc.data.id === hovered && setHovered(null);
              }}
              onClick={() => {
                onClick(arc.data.id);
              }}
              $opacity={hovered !== null && hovered !== arc.data.id ? 0.5 : 1}
            />
          ))}
        </g>
      </StyledSvg>
      {unit && (
        <TextLayer>
          <Text as="div" variant="reportHeadlineLarge">
            {localizeValue(
              sum(active.length ? active : data, d => d.value),
              { abbrev: true },
            )}
          </Text>
          <Text as="div" variant="reportLabelSmall">
            {unit}
          </Text>
        </TextLayer>
      )}
    </ChartLayer>
  );
};
